import { isDesktop, isMobile } from '@/lib/platform';
import pkg from '../../../package.json';

export type IntercomIdentity = { appId: string; userJwt: string };

type IntercomFn = ((...args: unknown[]) => void) & {
  q?: unknown[][];
  c?: (args: unknown) => void;
  booted?: boolean;
};

const API_BASE = 'https://api-iam.intercom.io';

let booted = false;

function platformName(): string {
  if (isDesktop) return 'desktop';
  if (isMobile) return 'mobile';
  return 'web';
}

function intercomFn(): IntercomFn | undefined {
  const fn = (window as unknown as { Intercom?: unknown }).Intercom;
  return typeof fn === 'function' ? (fn as IntercomFn) : undefined;
}

/**
 * Intercom's own snippet installs this shim so commands issued before the
 * widget finishes loading are buffered and replayed rather than dropped.
 */
function installQueueStub(): IntercomFn {
  const existing = intercomFn();
  if (existing) return existing;

  const stub = function (...args: unknown[]) {
    stub.c?.(args);
  } as IntercomFn;
  stub.q = [];
  stub.c = (args: unknown) => {
    stub.q?.push(args as unknown[]);
  };
  (window as unknown as { Intercom: IntercomFn }).Intercom = stub;
  return stub;
}

export function bootIntercom(identity: IntercomIdentity): void {
  if (booted) return;
  booted = true;

  try {
    const intercom = installQueueStub();
    intercom('boot', {
      app_id: identity.appId,
      api_base: API_BASE,
      intercom_user_jwt: identity.userJwt,
      hide_default_launcher: true,
      platform: platformName(),
      app_version: pkg.version,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${identity.appId}`;
    script.setAttribute('data-intercom', 'true');
    script.addEventListener('error', () => {
      script.remove();
      booted = false;
    });
    document.head.appendChild(script);
  } catch {
    // Silent by design — the design's failure contract requires every boot
    // failure to degrade quietly, and this runs inside the bridge's
    // setTimeout where an uncaught throw has no caller to report to.
    booted = false;
  }
}

export function openMessenger(): boolean {
  const intercom = intercomFn();
  if (!intercom) return false;
  intercom('show');
  return intercom.booted === true;
}

export function shutdownIntercom(): void {
  intercomFn()?.('shutdown');
  delete (window as unknown as { Intercom?: IntercomFn }).Intercom;
  document.querySelector('script[data-intercom]')?.remove();
  booted = false;
}

/**
 * The booted flag is module-private state with no other way to reach it from
 * tests, so a fresh call must reset it between cases instead of reloading
 * the module.
 */
export function resetIntercomForTests(): void {
  booted = false;
}
